<template>
  <div class="imageViewerOverlay" @click.self="closeViewer">
    <div class="imageViewerTopbar">
      <span class="zoomLabel">{{ Math.round(zoom * 100) }}%</span>
      <CButton size="sm" color="danger" class="ml-3" @click="closeViewer">Close</CButton>
    </div>

    <div class="imageViewerStage" @wheel.prevent="handleWheel">
      <img
        v-if="currentImage"
        :src="currentImage"
        class="viewerImage"
        :class="{ isDragging: draggingFocus }"
        :style="imageStyle"
        draggable="false"
        ref="viewerImage"
        @dblclick="toggleZoomAtPoint"
        @mousedown.prevent="startFocusDrag"
        @dragstart.prevent
      />
    </div>

    <div class="imageViewerToolbar">
      <CButton size="sm" color="light" class="mr-2" @click="zoomOut">-</CButton>
      <CButton size="sm" color="light" class="mr-2" @click="zoomIn">+</CButton>
      <CButton size="sm" color="light" class="mr-2" @click="resetZoom">Reset</CButton>
      <CButton size="sm" color="light" class="mr-2" v-if="hasMultipleImages" @click="previousImage">Prev</CButton>
      <CButton size="sm" color="light" v-if="hasMultipleImages" @click="nextImage">Next</CButton>
    </div>
  </div>
</template>

<script>
var methods = {}

methods.closeViewer = function () {
  this.$store.commit('setImageViewer', false)
}

methods.zoomIn = function () {
  this.zoom = Math.min(this.zoom + 0.5, 5)
}

methods.zoomOut = function () {
  this.zoom = Math.max(this.zoom - 0.5, 0.5)
}

methods.resetZoom = function () {
  this.zoom = 1
  this.originX = 50
  this.originY = 50
}

methods.toggleZoomAtPoint = function (event) {
  this.updateOriginFromEvent(event)
  if (this.zoom === 1) {
    this.zoom = 2
  } else {
    this.zoom = 1
    this.originX = 50
    this.originY = 50
  }
}

methods.handleWheel = function (event) {
  this.updateOriginFromEvent(event)
  if (event.deltaY < 0) {
    this.zoomIn()
  } else {
    this.zoomOut()
  }
}

methods.previousImage = function () {
  if (!this.hasMultipleImages) {
    return
  }
  this.currentIndex = this.currentIndex === 0 ? this.images.length - 1 : this.currentIndex - 1
  this.resetZoom()
}

methods.nextImage = function () {
  if (!this.hasMultipleImages) {
    return
  }
  this.currentIndex = this.currentIndex === this.images.length - 1 ? 0 : this.currentIndex + 1
  this.resetZoom()
}

methods.updateOriginFromEvent = function (event) {
  if (!this.$refs.viewerImage) {
    return
  }

  const rect = this.$refs.viewerImage.getBoundingClientRect()
  if (!rect.width || !rect.height) {
    return
  }

  const x = ((event.clientX - rect.left) / rect.width) * 100
  const y = ((event.clientY - rect.top) / rect.height) * 100

  this.originX = Math.min(Math.max(x, 0), 100)
  this.originY = Math.min(Math.max(y, 0), 100)
}

methods.startFocusDrag = function (event) {
  if (!this.canPan) {
    return
  }

  this.draggingFocus = true
  this.updateOriginFromEvent(event)
}

methods.handleFocusDrag = function (event) {
  if (!this.draggingFocus) {
    return
  }

  this.updateOriginFromEvent(event)
}

methods.stopFocusDrag = function () {
  this.draggingFocus = false
}

methods.handleKeydown = function (event) {
  if (event.key === 'Escape') {
    this.closeViewer()
  } else if (event.key === '+' || event.key === '=') {
    this.zoomIn()
  } else if (event.key === '-') {
    this.zoomOut()
  } else if (event.key === 'ArrowLeft') {
    this.previousImage()
  } else if (event.key === 'ArrowRight') {
    this.nextImage()
  }
}

export default {
  name: 'ImageViewer',
  data () {
    return {
      zoom: 1,
      currentIndex: 0,
      originX: 50,
      originY: 50,
      draggingFocus: false
    }
  },
  computed: {
    images () {
      return this.$store.state.image || []
    },
    currentImage () {
      return this.images[this.currentIndex] || null
    },
    hasMultipleImages () {
      return this.images.length > 1
    },
    canPan () {
      return this.zoom > 1
    },
    imageStyle () {
      return {
        transform: 'scale(' + this.zoom + ')',
        transformOrigin: this.originX + '% ' + this.originY + '%'
      }
    }
  },
  watch: {
    '$store.state.imageIndex': function (newVal) {
      this.currentIndex = typeof newVal === 'number' ? newVal : 0
      this.resetZoom()
    },
    '$store.state.image': function () {
      this.currentIndex = typeof this.$store.state.imageIndex === 'number' ? this.$store.state.imageIndex : 0
      this.resetZoom()
    }
  },
  mounted () {
    this.currentIndex = typeof this.$store.state.imageIndex === 'number' ? this.$store.state.imageIndex : 0
    window.addEventListener('keydown', this.handleKeydown)
    window.addEventListener('mousemove', this.handleFocusDrag)
    window.addEventListener('mouseup', this.stopFocusDrag)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('mousemove', this.handleFocusDrag)
    window.removeEventListener('mouseup', this.stopFocusDrag)
  },
  methods: methods
}
</script>

<style scoped>
.imageViewerOverlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  flex-direction: column;
}

.imageViewerTopbar {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(20, 20, 20, 0.92);
}

.zoomLabel {
  color: #fff;
  min-width: 56px;
  text-align: center;
}

.imageViewerStage {
  flex: 1;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.imageViewerToolbar {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(20, 20, 20, 0.92);
}

.viewerImage {
  max-width: 90vw;
  max-height: 85vh;
  transition: transform 0.12s ease-out;
  cursor: zoom-in;
  user-select: none;
}

.viewerImage.isDragging {
  cursor: crosshair;
  transition: none;
}
</style>
